import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronDown,
  Check,
  Copy,
  ExternalLink,
  ArrowRight,
  MessageCircle,
  Bot,
  Code,
  Settings,
  Users,
  BarChart3,
  Palette,
  Shield,
  GitBranch,
  BookOpen,
  Globe,
  Zap,
  Star,
  Clock,
  Mail,
  Phone,
  FileText,
  Image,
  Video,
  ShoppingBag,
  Calendar,
  ListChecks,
  Upload,
  Webhook,
  Sheet,
  Lock,
  Eye,
  Download,
  Bell,
  Rocket,
  Wrench,
  BrainCircuit,
  ClipboardList,
  Link,
  Cable,
  Lightbulb,
  AlertTriangle,
  XCircle,
  Hand,
  HelpCircle,
} from "lucide-react";
import BRAND from "../theme/talktokam";

const SectionCard = ({ id, icon: Icon, title, children }) => (
  <Box id={id} sx={{ scrollMarginTop: 100 }}>
    <Box
      sx={{
        p: { xs: 2.5, md: 4 },
        borderRadius: 4,
        bgcolor: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.06)",
        mb: 3,
        transition: "all 0.3s",
        "&:hover": {
          borderColor: `${BRAND.cyan}33`,
          boxShadow: `0 8px 40px ${BRAND.cyan}08`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: `${BRAND.cyan}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={20} color={BRAND.cyan} />
        </Box>
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#fff" }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  </Box>
);

const SubAccordion = ({ title, defaultExpanded, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    sx={{
      bgcolor: "rgba(255,255,255,0.02)",
      borderRadius: "12px !important",
      border: "1px solid rgba(255,255,255,0.06)",
      mb: 1.5,
      "&:before": { display: "none" },
      "&.Mui-expanded": { borderColor: `${BRAND.cyan}33` },
    }}
  >
    <AccordionSummary
      expandIcon={<ChevronDown size={18} color="#94a3b8" />}
      sx={{ "& .MuiAccordionSummary-content": { my: 1.2 } }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#e2e8f0" }}
      >
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pb: 2, pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
);

const StepList = ({ steps }) => (
  <Box sx={{ mb: 2 }}>
    {steps.map((s, i) => (
      <Box
        key={i}
        sx={{ display: "flex", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            bgcolor: `${BRAND.cyan}22`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.1,
          }}
        >
          <Typography
            sx={{ fontSize: "0.7rem", fontWeight: 700, color: BRAND.cyan }}
          >
            {i + 1}
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: "0.85rem", color: "#cbd5e1", lineHeight: 1.6 }}
        >
          {s}
        </Typography>
      </Box>
    ))}
  </Box>
);

const ConfigTable = ({ rows }) => (
  <Box
    sx={{
      overflowX: "auto",
      mb: 2,
      borderRadius: 2,
      border: "1px solid rgba(255,255,255,0.06)",
    }}
  >
    <Box sx={{ minWidth: 500 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          bgcolor: "rgba(255,255,255,0.03)",
          px: 2,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: BRAND.cyan,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Setting
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: BRAND.cyan,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Description
        </Typography>
      </Box>
      {rows.map((r, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            px: 2,
            py: 0.8,
            borderTop: "1px solid rgba(255,255,255,0.04)",
          }}
        >
          <Typography
            sx={{ fontSize: "0.8rem", color: "#e2e8f0", fontWeight: 600 }}
          >
            {r[0]}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
            {r[1]}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const TipBox = ({ type, text }) => {
  const colors = {
    tip: { bg: "#10b981", icon: Lightbulb, color: "#10b981" },
    warn: { bg: "#f59e0b", icon: AlertTriangle, color: "#f59e0b" },
    error: { bg: "#ef4444", icon: XCircle, color: "#ef4444" },
  };
  const c = colors[type] || colors.tip;
  const IconComp = c.icon;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        bgcolor: `${c.bg}0d`,
        border: `1px solid ${c.bg}22`,
        mb: 2,
      }}
    >
      <Box sx={{ flexShrink: 0, mt: 0.1 }}>
        <IconComp size={18} color={c.color} />
      </Box>
      <Typography
        sx={{ fontSize: "0.8rem", color: "#cbd5e1", lineHeight: 1.6 }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const MockWidgetPreview = () => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#0a0e27",
      border: "1px solid rgba(255,255,255,0.08)",
      maxWidth: 300,
      mx: "auto",
      mb: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pb: 1.5,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Bot size={16} color="#fff" />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}>
          Support Bot
        </Typography>
        <Typography sx={{ fontSize: "0.6rem", color: "#10b981" }}>
          ● Online
        </Typography>
      </Box>
    </Box>
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: "0 10px 10px 10px",
            bgcolor: "rgba(255,255,255,0.06)",
            maxWidth: "80%",
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
            Hi! How can I help you today?
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: "10px 0 10px 10px",
            background: `linear-gradient(135deg, ${BRAND.cyan}33, ${BRAND.purple}33)`,
            maxWidth: "80%",
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", color: "#fff" }}>
            I need help with my order
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Box
          sx={{
            p: 1,
            borderRadius: "0 10px 10px 10px",
            bgcolor: "rgba(255,255,255,0.06)",
            maxWidth: "80%",
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>
            Sure! Let me check that for you.
          </Typography>
        </Box>
      </Box>
    </Box>
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {["Track", "FAQ", "Human"].map((c, i) => (
        <Box
          key={i}
          sx={{
            px: 1,
            py: 0.4,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>
            {c}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const MockFlowBuilder = () => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#0a0e27",
      border: "1px solid rgba(255,255,255,0.08)",
      mb: 2,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pb: 1,
        mb: 1.5,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <Typography
        sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8" }}
      >
        Flow: Main Support Flow
      </Typography>
      <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
        <Chip
          label="Save"
          size="small"
          sx={{
            bgcolor: `${BRAND.cyan}22`,
            color: BRAND.cyan,
            fontSize: "0.6rem",
            height: 22,
          }}
        />
      </Box>
    </Box>
    {[
      { icon: Hand, label: "Welcome Message", color: "#10b981" },
      {
        icon: HelpCircle,
        label: "What do you need help with?",
        color: BRAND.cyan,
      },
      { icon: ClipboardList, label: "Order Tracking", color: BRAND.purple },
      { icon: Phone, label: "Connect to Agent", color: "#f59e0b" },
    ].map((n, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 0.5,
          position: "relative",
        }}
      >
        {i > 0 && (
          <Box
            sx={{ width: 1, height: 20, bgcolor: `${n.color}44`, ml: 2.5 }}
          />
        )}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: `${n.color}18`,
            border: `1px solid ${n.color}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <n.icon size={18} color={n.color} />
        </Box>
        <Typography
          sx={{ fontSize: "0.75rem", color: "#cbd5e1", fontWeight: 500 }}
        >
          {n.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const MockAnalytics = () => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#0a0e27",
      border: "1px solid rgba(255,255,255,0.08)",
      mb: 2,
    }}
  >
    <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
      {[
        { label: "Total Chats", value: "1,247", color: BRAND.cyan },
        { label: "Leads", value: "384", color: BRAND.purple },
        { label: "Rating", value: "4.8★", color: "#10b981" },
      ].map((s, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            p: 1,
            borderRadius: 1.5,
            bgcolor: "rgba(255,255,255,0.03)",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "0.55rem", color: "#64748b", mb: 0.2 }}>
            {s.label}
          </Typography>
          <Typography
            sx={{ fontSize: "1rem", fontWeight: 700, color: s.color }}
          >
            {s.value}
          </Typography>
        </Box>
      ))}
    </Box>
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 60 }}>
      {[35, 50, 28, 65, 45, 55, 38, 70, 52, 48, 60, 42, 58, 33].map((h, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            height: h * 0.8,
            borderRadius: "3px 3px 0 0",
            background: `linear-gradient(180deg, ${BRAND.cyan}88, ${BRAND.cyan}33)`,
            minHeight: 4,
          }}
        />
      ))}
    </Box>
  </Box>
);

const MockAgentChat = () => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#0a0e27",
      border: "1px solid rgba(255,255,255,0.08)",
      mb: 2,
      maxWidth: 350,
      mx: "auto",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pb: 1,
        mb: 1,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: "#10b981",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#fff" }}
        >
          A
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#fff" }}>
          Agent - Aman
        </Typography>
        <Typography sx={{ fontSize: "0.55rem", color: "#10b981" }}>
          ● Online
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {[Bell, Check].map((Icon, i) => (
          <Icon key={i} size={14} color="#64748b" />
        ))}
      </Box>
    </Box>
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 0.5 }}>
        <Box
          sx={{
            p: 0.8,
            borderRadius: "0 8px 8px 8px",
            bgcolor: "rgba(255,255,255,0.06)",
          }}
        >
          <Typography sx={{ fontSize: "0.65rem", color: "#cbd5e1" }}>
            Hi, I need help with billing
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
        <Box
          sx={{
            p: 0.8,
            borderRadius: "8px 0 8px 8px",
            bgcolor: `${BRAND.cyan}22`,
          }}
        >
          <Typography sx={{ fontSize: "0.65rem", color: "#fff" }}>
            Sure! Let me check your account
          </Typography>
        </Box>
      </Box>
    </Box>
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Box
        sx={{
          flex: 1,
          p: 0.6,
          borderRadius: 1,
          bgcolor: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Typography sx={{ fontSize: "0.6rem", color: "#64748b" }}>
          Type a message...
        </Typography>
      </Box>
      <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: `${BRAND.cyan}22` }}>
        <ArrowRight size={14} color={BRAND.cyan} />
      </Box>
    </Box>
  </Box>
);

const MockKnowledgeBase = () => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#0a0e27",
      border: "1px solid rgba(255,255,255,0.08)",
      mb: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pb: 1,
        mb: 1.5,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <BookOpen size={16} color={BRAND.cyan} />
      <Typography
        sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#e2e8f0" }}
      >
        Knowledge Base
      </Typography>
      <Chip
        label="3 documents"
        size="small"
        sx={{
          bgcolor: `${BRAND.cyan}18`,
          color: BRAND.cyan,
          fontSize: "0.55rem",
          height: 20,
          ml: "auto",
        }}
      />
    </Box>
    {[
      { name: "Product_Guide.pdf", status: "✅ Indexed", chunks: 12 },
      { name: "FAQ_2024.pdf", status: "✅ Indexed", chunks: 8 },
      { name: "talktokam.com", status: "🔄 Crawling...", chunks: 24 },
    ].map((d, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          p: 1,
          borderRadius: 1,
          bgcolor: "rgba(255,255,255,0.02)",
          mb: 0.5,
        }}
      >
        <FileText size={14} color="#94a3b8" />
        <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1", flex: 1 }}>
          {d.name}
        </Typography>
        <Typography sx={{ fontSize: "0.6rem", color: "#94a3b8" }}>
          {d.chunks} chunks
        </Typography>
        <Typography sx={{ fontSize: "0.6rem", color: "#10b981" }}>
          {d.status}
        </Typography>
      </Box>
    ))}
  </Box>
);

const TOC_SECTIONS = [
  { id: "getting-started", icon: Rocket, title: "Getting Started" },
  { id: "flow-builder", icon: Wrench, title: "Flow Builder" },
  { id: "ai-agents", icon: BrainCircuit, title: "AI Agents" },
  { id: "ai-bot-knowledge", icon: Bot, title: "AI Bot & Knowledge Base" },
  { id: "live-chat", icon: MessageCircle, title: "Live Chat" },
  { id: "analytics", icon: BarChart3, title: "Analytics" },
  { id: "widget-customization", icon: Palette, title: "Widget Customization" },
  { id: "team-users", icon: Users, title: "Team & Users" },
  {
    id: "leads-conversations",
    icon: ClipboardList,
    title: "Leads & Conversations",
  },
  { id: "integrations", icon: Link, title: "Integrations" },
  { id: "account-billing", icon: Settings, title: "Account & Billing" },
  { id: "api-reference", icon: Cable, title: "API Reference" },
];

const Docs = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 8, md: 12 },
          overflow: "hidden",
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              Build Better Bots. Faster.
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#94a3b8",
                mb: 3,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Complete guide to everything TalkToKam — from your first bot to
              advanced features.
            </Typography>
            <TextField
              fullWidth
              placeholder="Search documentation..."
              size="medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="#64748b" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  bgcolor: "rgba(255,255,255,0.06)",
                  "& fieldset": { border: "1px solid rgba(255,255,255,0.1)" },
                  "& input": { color: "#cbd5e1", fontSize: "0.95rem" },
                  "&:hover fieldset": { borderColor: `${BRAND.cyan}44` },
                  "&.Mui-focused fieldset": { borderColor: BRAND.cyan },
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "center",
                mt: 3,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/api-docs")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  py: 1.2,
                  background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                  boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                  "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
                  fontSize: "0.9rem",
                }}
              >
                <Code size={16} style={{ marginRight: 8 }} /> API Reference
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/help-center")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  borderColor: "rgba(255,255,255,0.2)",
                  color: "#cbd5e1",
                  fontSize: "0.9rem",
                  "&:hover": {
                    borderColor: "#fff",
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                <MessageCircle size={16} style={{ marginRight: 8 }} /> Help
                Center
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: "sticky",
                top: 90,
                display: { xs: "none", md: "block" },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 2,
                }}
              >
                Table of Contents
              </Typography>
              {TOC_SECTIONS.map((s) => (
                <Box
                  key={s.id}
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.6,
                    px: 1.5,
                    borderRadius: 1,
                    textDecoration: "none",
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.04)",
                      color: BRAND.cyan,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <s.icon size={16} color="#94a3b8" />
                  </Box>
                  <Typography sx={{ fontSize: "0.8rem" }}>{s.title}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                gap: 0.5,
                mb: 3,
                overflowX: "auto",
                pb: 1,
              }}
            >
              {TOC_SECTIONS.map((s) => (
                <Box
                  key={s.id}
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    textDecoration: "none",
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    whiteSpace: "nowrap",
                    "&:hover": { borderColor: BRAND.cyan },
                  }}
                >
                  <s.icon size={14} color="#94a3b8" />
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    {s.title}
                  </Typography>
                </Box>
              ))}
            </Box>

            <SectionCard
              id="getting-started"
              icon={Rocket}
              title="Getting Started"
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                TalkToKam is an AI chatbot platform that lets you build
                intelligent chatbots in minutes. This guide covers every step
                from account creation to widget deployment.
              </Typography>

              <SubAccordion title="Step 1: Create Your Account" defaultExpanded>
                <StepList
                  steps={[
                    "Go to talktokam.com",
                    "Click 'Get Started Free' in the top-right corner",
                    "Enter your full name, email address, and password",
                    "Click 'Create Account'",
                    "Check your email inbox and click the verification link",
                    "Log in — you'll land on the Dashboard",
                  ]}
                />
                <TipBox
                  type="tip"
                  text="Didn't receive the verification email? Check your spam folder or use 'Resend Verification'."
                />
              </SubAccordion>

              <SubAccordion title="Step 2: Dashboard Tour">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  The Dashboard is your command center. All sections are
                  available in the left sidebar:
                </Typography>
                <ConfigTable
                  rows={[
                    [
                      "Dashboard",
                      "Key metrics: total chats, leads, ratings — at a glance",
                    ],
                    [
                      "Bots",
                      "List of all chatbots — create, edit, activate/deactivate",
                    ],
                    ["Agents", "Manage AI Agents (Sales, Support, FAQ, etc.)"],
                    ["Live Chat", "Real-time conversations with visitors"],
                    ["Analytics", "Detailed reports and charts"],
                    ["Team", "Add/remove live agents"],
                    [
                      "Settings",
                      "Account, billing, integrations, and configuration",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Step 3: Create Your First Bot">
                <StepList
                  steps={[
                    "Click 'Create Bot' on the Dashboard",
                    "Enter a name for your bot (e.g., 'Customer Support Bot')",
                    "Select an industry — this will determine the template",
                    "Click 'Create' — your bot is ready",
                    "You'll now be on the Settings page — configure your bot here",
                  ]}
                />
                <MockFlowBuilder />
              </SubAccordion>

              <SubAccordion title="Step 4: Bot Settings Explained">
                <ConfigTable
                  rows={[
                    ["Bot Name", "Display name shown in the widget"],
                    ["Status", "Active / Inactive — toggle to turn bot on/off"],
                    [
                      "Theme Colors",
                      "Header gradient, option buttons, tab colors",
                    ],
                    [
                      "Auto Trigger",
                      "Desktop delay (sec), mobile delay (sec), exit-intent",
                    ],
                    [
                      "Domain Whitelist",
                      "Widget only works on whitelisted domains",
                    ],
                    ["Language", "Choose bot language (10+ Indian languages)"],
                    [
                      "Consent Mode",
                      "GDPR consent: All Countries / EU Only / Disabled",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Step 5: Embed Widget on Your Website">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Bot ready? You can deploy it on your website in two ways:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: BRAND.cyan,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Method 1: JavaScript Embed
                </Typography>
                <StepList
                  steps={[
                    "Go to Installation page (bot Settings → Installation)",
                    "Whitelist your domain (e.g., yourwebsite.com, localhost)",
                    "Copy the embed code",
                    "Paste the code into your website's &lt;body&gt; tag (preferably footer)",
                  ]}
                />
                <TipBox
                  type="tip"
                  text="The widget may take 1-2 minutes to appear the first time. Clear your browser cache and check again."
                />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#0a0e27",
                    border: "1px solid rgba(255,255,255,0.06)",
                    mb: 2,
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    color: "#94a3b8",
                    overflowX: "auto",
                  }}
                >
                  &lt;script
                  src="https://app.talktokam.com/widget.js?id=ABC123"&gt;&lt;/script&gt;
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: BRAND.cyan,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Method 2: Direct Link
                </Typography>
                <Typography
                  sx={{ fontSize: "0.8rem", color: "#94a3b8", lineHeight: 1.6 }}
                >
                  If you don't have a website, you can share a direct link. Copy
                  the "Direct Link" from the Installation page and share it via
                  WhatsApp, email, or social media.
                </Typography>
              </SubAccordion>

              <SubAccordion title="Troubleshooting">
                <TipBox
                  type="error"
                  text="Widget not showing? → (1) Check domain whitelist (2) Is bot status 'Active'? (3) Check browser console for errors (F12 → Console)"
                />
                <TipBox
                  type="warn"
                  text="Bot responding slowly? → The AI model may be heavy. Enable the 'Optimize for Speed' option in Settings."
                />
              </SubAccordion>
            </SectionCard>

            <SectionCard id="flow-builder" icon={Wrench} title="Flow Builder">
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Flow Builder lets you create chatbot conversation flows using a
                drag-and-drop interface. 15+ question types available.
              </Typography>

              <SubAccordion title="Question Types Reference" defaultExpanded>
                <ConfigTable
                  rows={[
                    [
                      "Text / Info",
                      "Simple text message — no input required",
                      "Free",
                    ],
                    [
                      "Multiple Choice",
                      "Option buttons — row/column layout, image choices, 'Other' option",
                      "Free",
                    ],
                    [
                      "Single Choice",
                      "Dropdown or list view — auto-submit on select",
                      "Free",
                    ],
                    [
                      "Rating",
                      "Star (1-5), Smiley (3), or Number (1-10) rating",
                      "Free",
                    ],
                    [
                      "Date Picker",
                      "Native date input — calendar picker",
                      "Free",
                    ],
                    ["Time Picker", "Native time input — hour:minute", "Free"],
                    [
                      "Range Slider",
                      "Slider with min/max values and labels",
                      "Free",
                    ],
                    [
                      "Website / URL",
                      "URL input with https:// validation",
                      "Free",
                    ],
                    [
                      "Email",
                      "Email input with strict regex validation",
                      "Free",
                    ],
                    ["Phone", "Phone number input with validation", "Free"],
                    [
                      "Location",
                      "Text input with 'Share Location' hint",
                      "Free",
                    ],
                    ["Number", "Numeric input with min/max validation", "Free"],
                    [
                      "File Upload",
                      "PDF/Image upload via Cloudinary",
                      "Starter+",
                    ],
                    [
                      "Conditional Logic",
                      "Jump to different flow based on answer",
                      "Starter+",
                    ],
                    [
                      "Dynamic Questions",
                      "Load live options from API",
                      "Growth+",
                    ],
                    [
                      "Real Time Search",
                      "Live search dropdown — from API source",
                      "Growth+",
                    ],
                    [
                      "Carousel",
                      "Product cards with images, title, button",
                      "Enterprise",
                    ],
                    [
                      "Order Items",
                      "E-commerce flow — items, qty, price, currency",
                      "Enterprise",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Variables & Data Referencing">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  You can reference user answers anywhere in the flow using
                  variables.
                </Typography>
                <ConfigTable
                  rows={[
                    [
                      "{Q1}, {Q2}, ...",
                      "Reference user's answer by question number",
                    ],
                    ["{name}", "User's name (if 'Ask Name' option is enabled)"],
                    ["{email}", "User's email address (if captured)"],
                    ["{phone}", "User's phone number (if captured)"],
                    [
                      "{greeting}",
                      "Time-based greeting: Good Morning/Afternoon/Evening",
                    ],
                    ["{time_emoji}", "Time-based emoji: 🌅/☀️/🌙"],
                  ]}
                />
                <TipBox
                  type="tip"
                  text="Example: '{greeting} {name}! Your order is confirmed.' → 'Good Morning Aman! Your order is confirmed.'"
                />
              </SubAccordion>

              <SubAccordion title="Conditional Logic (Starter+)">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  If the user gives a specific answer, you can route them to a
                  different flow. For example:
                </Typography>
                <StepList
                  steps={[
                    'Question: "Do you need help?"',
                    'Option 1: "Order Status" → jump to order tracking flow',
                    'Option 2: "Refund" → jump to refund flow',
                    'Option 3: "Talk to Agent" → jump to live chat handoff',
                    "For each option, use 'Add Condition' to select the target question",
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Skip Logic & Validation">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  You can allow users to skip questions (mark as optional) or
                  add validations:
                </Typography>
                <ConfigTable
                  rows={[
                    ["Required toggle", "User cannot skip the question"],
                    ["Min/Max (Number)", "Number range validation"],
                    ["Email validation", "Strict email format check"],
                    ["URL validation", "https:// required check"],
                    ["Pattern matching", "Custom regex pattern validation"],
                  ]}
                />
              </SubAccordion>
            </SectionCard>

            <SectionCard id="ai-agents" icon={BrainCircuit} title="AI Agents">
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                AI Agents are powerful pre-built chatbot templates powered by
                Groq/OpenAI. They answer visitor questions based on your actual
                website content.
              </Typography>

              <SubAccordion title="5 Pre-built Templates" defaultExpanded>
                <ConfigTable
                  rows={[
                    [
                      "Sales Agent",
                      "Lead generation and product inquiries",
                      "All Plans",
                    ],
                    [
                      "Support Agent",
                      "Customer support, troubleshooting, and FAQs",
                      "All Plans",
                    ],
                    [
                      "Lead Qualifier",
                      "Qualifies visitors — budget, timeline, needs",
                      "All Plans",
                    ],
                    [
                      "Appointment Agent",
                      "Calendar booking and appointment scheduling",
                      "All Plans",
                    ],
                    [
                      "FAQ Agent",
                      "Instant answers to frequently asked questions",
                      "All Plans",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Create & Configure AI Agent">
                <StepList
                  steps={[
                    "Go to Agents page → Click 'Create Agent'",
                    "Enter agent name (e.g., 'Sales Bot')",
                    "Choose a template (Sales, Support, Lead Qualifier, FAQ, Appointment)",
                    "Customize the system prompt — define the agent's behavior",
                    "Add suggested questions — these will appear as chips",
                    "Choose avatar image and theme color",
                    "Upload training data or crawl your website",
                    "Click 'Save' — agent is ready!",
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Lead Capture & Human Handoff">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  The AI Agent automatically detects email and phone numbers
                  from visitor messages. You can view these leads in the
                  Contacts Dashboard.
                </Typography>
                <StepList
                  steps={[
                    "When a visitor types an email/phone, the agent auto-detects it",
                    "A lead notification is immediately sent to your email",
                    "You can join the agent conversation using 'Owner Reply' feature",
                    "If the agent encounters a complex query, it triggers [CONNECT_HUMAN] keyword",
                    "The visitor sees 'A human agent has joined!' notification",
                  ]}
                />
                <MockAgentChat />
              </SubAccordion>

              <SubAccordion title="Agent Chats Dashboard">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  All AI agent conversations are visible in one dashboard — with
                  search, filter, stats, and resolve options.
                </Typography>
                <ConfigTable
                  rows={[
                    ["Search", "Search by user name or message content"],
                    ["Filter", "Filter by status (active/resolved)"],
                    [
                      "Visitor Info",
                      "Name, email, phone, messages count, session ID",
                    ],
                    ["Stats", "Today's chats, active, resolved, total"],
                    ["Resolve", "Mark conversation as resolved"],
                    [
                      "Owner Reply",
                      "Reply directly to visitor (5 sec polling)",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Best Practices">
                <TipBox
                  type="tip"
                  text="Keep the system prompt clear and specific. Example: 'You are a sales assistant that explains SaaS products. You cannot share pricing.'"
                />
                <TipBox
                  type="tip"
                  text="Add 3-5 suggested questions to guide visitors."
                />
                <TipBox
                  type="warn"
                  text="Do not include sensitive information in training data. The AI agent follows strict rules."
                />
              </SubAccordion>
            </SectionCard>

            <SectionCard
              id="ai-bot-knowledge"
              icon={Bot}
              title="AI Bot & Knowledge Base"
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                The AI Bot activates after the flow builder. It answers visitor
                questions accurately based on your PDF documents and crawled
                website content. This feature is called RAG (Retrieval-Augmented
                Generation).
              </Typography>

              <SubAccordion title="AI Bot Settings" defaultExpanded>
                <ConfigTable
                  rows={[
                    ["Enable AI", "AI Bot on/off toggle"],
                    ["API Key", "Enter your OpenAI or Groq API key"],
                    ["Model", "Select model (GPT-4, GPT-3.5, Mixtral, etc.)"],
                    [
                      "System Prompt",
                      "Instructions for bot behavior — keep clear and specific",
                    ],
                    [
                      "Industry",
                      "Select industry — gives the bot domain context",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="PDF Knowledge Upload">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Upload your business PDF documents. The AI bot will
                  automatically extract text, divide it into chunks, and create
                  embeddings. When a visitor asks a question, relevant chunks
                  are retrieved and the AI answers based on them.
                </Typography>
                <StepList
                  steps={[
                    "Go to Settings → Knowledge Base tab",
                    "Click 'Upload PDF' button",
                    "Select PDF file (max size depends on plan)",
                    "Upload completes — bot processes automatically",
                    "View uploaded file in document list and preview chunks",
                  ]}
                />
                <MockKnowledgeBase />
                <ConfigTable
                  rows={[
                    ["File Types", "PDF, DOCX, TXT"],
                    [
                      "Processing",
                      "Auto text extraction → chunking → embedding",
                    ],
                    [
                      "Retrieval",
                      "BM25 keyword search + HuggingFace rerank (hybrid)",
                    ],
                    ["Free Plan", "3 documents max"],
                    ["Starter", "15 documents"],
                    ["Growth", "50 documents"],
                    ["Enterprise", "Unlimited"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Website Crawl (RAG)">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Enter your website URL and the AI bot will automatically crawl
                  your entire site. It extracts content, indexes it, and answers
                  visitor questions using your actual website content.
                </Typography>
                <StepList
                  steps={[
                    "Settings → Knowledge Base → 'Website Crawl' section",
                    "Enter your website URL (e.g., https://yourwebsite.com)",
                    "Select max pages (how many pages to crawl)",
                    "Click 'Start Crawl'",
                    "Watch the progress bar — crawling status updates in real-time",
                    "Crawl complete — AI bot now answers from website content",
                  ]}
                />
                <TipBox
                  type="warn"
                  text="React/Next.js websites use Puppeteer — this may take longer. Cheerio-based sites are faster."
                />
              </SubAccordion>

              <SubAccordion title="AI Message Limits per Plan">
                <ConfigTable
                  rows={[
                    ["Free", "50 AI messages/month", "3 docs + 3 crawls"],
                    ["Starter", "100 AI messages/month", "15 docs + 15 crawls"],
                    [
                      "Growth",
                      "1,000 AI messages/month",
                      "50 docs + 50 crawls",
                    ],
                    ["Enterprise", "Unlimited", "Unlimited"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Strict Mode — What It Means">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  When the AI bot doesn't have an answer (neither in PDF nor
                  website), it won't confidently give a wrong answer. Instead,
                  it will say:{" "}
                  <Box
                    component="span"
                    sx={{ color: BRAND.cyan, fontStyle: "italic" }}
                  >
                    "I don't have information about this"
                  </Box>
                  . This feature is called hallucination prevention.
                </Typography>
              </SubAccordion>
            </SectionCard>

            <SectionCard id="live-chat" icon={MessageCircle} title="Live Chat">
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                When the AI bot can't handle a complex query, the visitor can
                connect to a live agent. Full real-time chat system powered by
                Socket.io.
              </Typography>

              <SubAccordion title="Setup Live Chat" defaultExpanded>
                <StepList
                  steps={[
                    "Go to Settings → Live Chat section",
                    "Toggle 'Enable Live Chat' to ON",
                    "Go to Team → 'Add Agent' button to add agents",
                    "Agent logs in → sets status to 'Online'",
                    "When visitor selects 'Talk to Human', the agent gets notified",
                  ]}
                />
                <ConfigTable
                  rows={[
                    ["Chat Queue", "Visitor requests agent → goes to queue"],
                    [
                      "Auto-Assign",
                      "1 agent online → auto-assign; multiple → queue",
                    ],
                    ["Agent Status", "Online / Busy / Offline — toggle to set"],
                    ["Unread Badge", "Unread count shown in browser tab"],
                    [
                      "Notification Sound",
                      "Sound plays when new message arrives",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Agent Dashboard Walkthrough">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  The Agent dashboard displays the following:
                </Typography>
                <MockAgentChat />
                <ConfigTable
                  rows={[
                    [
                      "Stats Row",
                      "Today resolved, Active chats, Queue length, Total",
                    ],
                    [
                      "Conversation List",
                      "Left panel — all active conversations",
                    ],
                    [
                      "Chat Area",
                      "Center — messages with timestamps, seen ticks",
                    ],
                    [
                      "Visitor Info",
                      "Right panel — bot name, session ID, messages count",
                    ],
                    [
                      "Quick Actions",
                      "Transfer, Resolve, Canned Responses, CSV Export",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Canned Responses">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Pre-written replies that agents can send quickly. Access them
                  via shortcuts.
                </Typography>
                <StepList
                  steps={[
                    "Go to Settings → Canned Responses tab",
                    "Click 'Add Response'",
                    "Enter a shortcut (e.g., /greeting)",
                    "Write the message (e.g., 'Hi! Welcome to our support. How can I help?')",
                    "Type '/' in the agent dashboard to search shortcuts",
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Chat Transfer & Resolve">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  If an agent wants to transfer a chat to another agent, open
                  the transfer dialog. A list of online/offline agents will
                  appear. When a chat is resolved, the visitor will see a rating
                  prompt.
                </Typography>
              </SubAccordion>
            </SectionCard>

            <SectionCard
              id="analytics"
              icon={BarChart3}
              title="Analytics & Reports"
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                The Analytics dashboard gives you real-time insights —
                conversations, leads, ratings, and trends. (Starter+ plans)
              </Typography>

              <SubAccordion title="Dashboard Overview" defaultExpanded>
                <MockAnalytics />
                <ConfigTable
                  rows={[
                    [
                      "Total Conversations",
                      "Total chats — bot + live agent both counted",
                    ],
                    ["Total Messages", "Count of all messages"],
                    ["Active Chats", "Currently active conversations"],
                    ["Top User", "Visitor with the most conversations"],
                    [
                      "Avg Rating",
                      "Average star rating across all resolved chats",
                    ],
                    ["Trend Chart", "Conversation volume over 7/30/90 days"],
                    [
                      "Status Pie",
                      "Active vs Closed vs Live Requested distribution",
                    ],
                    [
                      "Message Metrics",
                      "Avg messages per chat, closed rate with progress rings",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Time Range & Filters">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  You can filter data by last 7 days, 30 days, or 90 days. All
                  charts update automatically. Export in JSON format is also
                  available.
                </Typography>
              </SubAccordion>
            </SectionCard>

            <SectionCard
              id="widget-customization"
              icon={Palette}
              title="Widget Customization"
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Customize the chatbot widget to match your brand — colors,
                fonts, trigger settings, consent mode, and more.
              </Typography>

              <SubAccordion title="Theme Colors" defaultExpanded>
                <ConfigTable
                  rows={[
                    [
                      "Header Gradient",
                      "Widget header background gradient color",
                    ],
                    ["Option Buttons", "Question options background color"],
                    ["Tab Indicator", "Active tab underline color"],
                    ["Save Button", "Save/submit button gradient"],
                    ["Font", "Widget font family (default: Plus Jakarta Sans)"],
                  ]}
                />
                <MockWidgetPreview />
              </SubAccordion>

              <SubAccordion title="Auto Trigger Settings">
                <ConfigTable
                  rows={[
                    [
                      "Desktop Delay",
                      "Seconds — how long before widget auto-shows (0 = instant)",
                    ],
                    ["Mobile Delay", "How long before widget shows on mobile"],
                    [
                      "Exit Intent",
                      "Trigger widget when user starts closing the page",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Consent Mode (GDPR)">
                <ConfigTable
                  rows={[
                    ["Mode", "All Countries / EU Only / Disabled"],
                    ["Custom Message", "Consent notice text"],
                    ["Privacy URL", "Privacy policy page link"],
                    ["Button Text", "Consent button text (default: 'Accept')"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Domain Whitelist">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  The widget will only show on whitelisted domains. Add each
                  domain on a new line. Example: yourwebsite.com,
                  app.yourwebsite.com. Embedding on any other domain will not
                  work.
                </Typography>
              </SubAccordion>
            </SectionCard>

            <SectionCard id="team-users" icon={Users} title="Team & Users">
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Add your team — agents, supervisors, and analysts. Each role has
                different permissions. (Starter+ plans: agent count depends on
                plan limit)
              </Typography>

              <SubAccordion title="User Roles" defaultExpanded>
                <ConfigTable
                  rows={[
                    [
                      "Admin",
                      "Full access — all features, settings, and users",
                    ],
                    ["Analyst", "Can only view analytics and reports"],
                    ["Supervisor", "Can monitor live chats and manage agents"],
                    ["Agent", "Handles live chat — own dashboard"],
                    ["User", "Default role — can view bot conversations"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Add Agent to Team">
                <StepList
                  steps={[
                    "Go to Team page",
                    "Click 'Add Agent' button",
                    "Enter agent details: name, email, password",
                    "Select role: Agent or Supervisor",
                    "Click 'Add' — the agent receives login credentials",
                    "Agent logs in → sets status to 'Online'",
                  ]}
                />
                <TipBox
                  type="tip"
                  text="Agent count depends on your plan's liveAgents limit. An upgrade prompt will appear when limit is reached."
                />
              </SubAccordion>

              <SubAccordion title="2FA Security (Growth+)">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Enhance your account security with two-factor authentication.
                  Scan the QR code using an authenticator app (Google
                  Authenticator, Authy, etc.).
                </Typography>
                <StepList
                  steps={[
                    "Go to Settings → Security tab",
                    "Toggle 'Enable 2FA' ON",
                    "Scan QR code with your authenticator app",
                    "Enter the 6-digit code from the app to verify",
                    "2FA enabled — code required on every login",
                  ]}
                />
              </SubAccordion>
            </SectionCard>

            <SectionCard
              id="leads-conversations"
              icon={ClipboardList}
              title="Leads & Conversations"
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Central hub for all visitor interactions and captured leads.
              </Typography>

              <SubAccordion title="Leads Dashboard" defaultExpanded>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  The AI bot automatically detects email and phone from visitor
                  messages. All leads appear in a table view — name, email,
                  phone, messages count, date, status. You can also search by
                  name or email.
                </Typography>
                <ConfigTable
                  rows={[
                    [
                      "Auto Extraction",
                      "Email/phone auto-detected from messages",
                    ],
                    [
                      "Lead Notifications",
                      "Email notification on new lead capture",
                    ],
                    [
                      "Hot Leads",
                      "Email+phone both captured → priority notification",
                    ],
                    ["CSV Export", "Download all leads as CSV file"],
                    [
                      "Contacts Dashboard",
                      "Unified view of all contacts (Starter+)",
                    ],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Conversations History">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  View all bot and live chat conversations here. Paginated list
                  with search, filter by bot, status filter
                  (active/closed/live-requested). Includes HTML rendering and
                  image display.
                </Typography>
              </SubAccordion>

              <SubAccordion title="Answers Dashboard">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  View all user responses to flow builder questions here. Browse
                  in an expandable accordion view and search by user name or
                  message content.
                </Typography>
              </SubAccordion>
            </SectionCard>

            <SectionCard id="integrations" icon={Link} title="Integrations">
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Connect your chatbot with other tools — webhooks, Google Sheets,
                WhatsApp, and CRM.
              </Typography>

              <SubAccordion
                title="Webhook Integrations (Starter+)"
                defaultExpanded
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Send conversation data to your custom webhook endpoint.
                  Perfect for real-time data sync.
                </Typography>
                <StepList
                  steps={[
                    "Settings → Webhooks tab",
                    "Click 'Add Webhook'",
                    "Enter webhook URL (your endpoint)",
                    "Select event (new_conversation, new_lead, chat_resolved)",
                    "Save — data will be sent on every event",
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Google Sheets Sync (Starter+)">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  Export conversation data directly to Google Sheets. Leads,
                  messages, and ratings — all update automatically in the sheet.
                </Typography>
              </SubAccordion>

              <SubAccordion title="WhatsApp (Growth+)">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  WhatsApp catalog and e-commerce features are available on
                  Growth+ plans. WhatsApp Business API integration coming
                  post-launch.
                </Typography>
              </SubAccordion>
            </SectionCard>

            <SectionCard
              id="account-billing"
              icon={Settings}
              title="Account & Billing"
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Manage your account — profile, plan, team, billing, and security
                settings.
              </Typography>

              <SubAccordion title="Profile Management" defaultExpanded>
                <ConfigTable
                  rows={[
                    ["Full Name", "Your display name"],
                    ["Email", "Login email (verified)"],
                    ["Phone", "Contact number"],
                    ["Website", "Your website URL"],
                    ["GST Number", "GSTIN for billing (India)"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Plan & Usage">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  On the Account page, you can view usage stats for your current
                  plan:
                </Typography>
                <ConfigTable
                  rows={[
                    ["Bots Used / Limit", "Bots created vs plan limit"],
                    ["Chats Used / Limit", "Monthly chat usage"],
                    ["AI Messages Remaining", "Remaining AI messages"],
                    ["Live Agents", "Agents added so far"],
                    ["Upgrade", "Upgrade to a higher plan"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Security">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  Change password, enable 2FA, or delete account. All available
                  under Settings → Security tab.
                </Typography>
                <TipBox
                  type="warn"
                  text="Deleting your account permanently removes all data — bots, conversations, agents, everything. This action cannot be undone."
                />
              </SubAccordion>
            </SectionCard>

            <SectionCard id="api-reference" icon={Cable} title="API Reference">
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#94a3b8",
                  mb: 2,
                  lineHeight: 1.7,
                }}
              >
                Comprehensive REST API for developers. All endpoints,
                authentication, and webhook schema available.
              </Typography>

              <SubAccordion title="Authentication" defaultExpanded>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 1,
                    lineHeight: 1.6,
                  }}
                >
                  Each API request requires a Bearer token. You can generate a
                  token from Settings → API section.
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#0a0e27",
                    border: "1px solid rgba(255,255,255,0.06)",
                    mb: 1,
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    color: "#94a3b8",
                    overflowX: "auto",
                  }}
                >
                  curl -X GET "https://api.talktokam.app/api/ai-agents" \<br />
                  {"  "}-H "Authorization: Bearer YOUR_API_TOKEN"
                </Box>
              </SubAccordion>

              <SubAccordion title="Key Endpoints">
                <ConfigTable
                  rows={[
                    ["GET /api/ai-agents", "List all AI agents"],
                    ["POST /api/ai-agents", "Create a new AI agent"],
                    ["GET /api/bots/:id/settings", "Bot configuration"],
                    ["GET /api/bots/:id/flow", "Bot conversation flow"],
                    [
                      "POST /api/conversation/init",
                      "Start a new conversation session",
                    ],
                    ["POST /api/conversation/ai-message", "Send message to AI"],
                    ["POST /api/webhooks", "Manage webhook endpoints"],
                    ["GET /api/analytics/overview", "Analytics data"],
                  ]}
                />
              </SubAccordion>

              <SubAccordion title="Webhook Payload Schema">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    lineHeight: 1.6,
                  }}
                >
                  When an event occurs (new lead, chat resolved), a JSON payload
                  is sent to your webhook endpoint. For full documentation,
                  check the{" "}
                  <Box
                    component="span"
                    sx={{ color: BRAND.cyan, cursor: "pointer" }}
                    onClick={() => navigate("/api-docs")}
                  >
                    API Docs page
                  </Box>
                  .
                </Typography>
              </SubAccordion>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/api-docs")}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    px: 3,
                    py: 1.2,
                    background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                    boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                    "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
                  }}
                >
                  <ExternalLink size={16} style={{ marginRight: 8 }} /> View
                  Full API Docs
                </Button>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Docs;
